import React, { useState, useRef, useCallback, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Play, Pause, Volume2, VolumeX, Loader2, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { apiService } from '@/services/api';

export interface AudioPlayerRef {
  seekTo: (seconds: number) => void;
}

interface AudioPlayerProps {
  appointmentId?: string;
  encounterId?: string;
}

interface PlaybackData {
  url: string;
  durationSeconds: number;
  mimeType: string;
  expiresAt: string;
}

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export const AudioPlayer = forwardRef<AudioPlayerRef, AudioPlayerProps>(
  ({ appointmentId, encounterId }, ref) => {
    const audioRef = useRef<HTMLAudioElement>(null);
    const progressRef = useRef<HTMLDivElement>(null);

    const [expanded, setExpanded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notAvailable, setNotAvailable] = useState(false);
    const [playbackData, setPlaybackData] = useState<PlaybackData | null>(null);

    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [muted, setMuted] = useState(false);

    const fetchPlaybackUrl = useCallback(async () => {
      if (!appointmentId && !encounterId) return;
      setLoading(true);
      setError(null);
      setNotAvailable(false);

      try {
        const endpoint = appointmentId
          ? `/v1/recordings/by-appointment/${appointmentId}/playback-url`
          : `/v1/recordings/by-encounter/${encounterId}/playback-url`;

        const response = await apiService.get<{ success: boolean; data: PlaybackData }>(endpoint);
        setPlaybackData((response as any).data);
      } catch (err: any) {
        const status = err?.response?.status || err?.status;
        if (status === 404) {
          setNotAvailable(true);
        } else {
          setError('Failed to load audio');
        }
      } finally {
        setLoading(false);
      }
    }, [appointmentId, encounterId]);

    // Lazy load: fetch URL only when expanded
    useEffect(() => {
      if (expanded && !playbackData && !loading && !notAvailable && !error) {
        fetchPlaybackUrl();
      }
    }, [expanded, playbackData, loading, notAvailable, error, fetchPlaybackUrl]);

    // Refresh URL if it expires during playback
    useEffect(() => {
      if (!playbackData?.expiresAt) return;
      const expiresAt = new Date(playbackData.expiresAt).getTime();
      const refreshAt = expiresAt - 60_000; // Refresh 1 min before expiry
      const delay = refreshAt - Date.now();
      if (delay <= 0) return;

      const timer = setTimeout(() => {
        fetchPlaybackUrl();
      }, delay);
      return () => clearTimeout(timer);
    }, [playbackData?.expiresAt, fetchPlaybackUrl]);

    // Expose seekTo via ref
    useImperativeHandle(ref, () => ({
      seekTo: (seconds: number) => {
        if (!expanded) setExpanded(true);
        if (audioRef.current) {
          audioRef.current.currentTime = seconds;
          if (!isPlaying) {
            audioRef.current.play().catch(() => {});
          }
        }
      }
    }), [expanded, isPlaying]);

    const handlePlayPause = useCallback(() => {
      const audio = audioRef.current;
      if (!audio) return;

      if (isPlaying) {
        audio.pause();
      } else {
        audio.play().catch(() => {});
      }
    }, [isPlaying]);

    const handleProgressClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
      const audio = audioRef.current;
      const bar = progressRef.current;
      if (!audio || !bar) return;

      const rect = bar.getBoundingClientRect();
      const fraction = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      audio.currentTime = fraction * duration;
    }, [duration]);

    const toggleMute = useCallback(() => {
      if (audioRef.current) {
        audioRef.current.muted = !muted;
        setMuted(!muted);
      }
    }, [muted]);

    const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

    if (!appointmentId && !encounterId) return null;

    return (
      <div className="mb-3">
        {/* Collapsed toggle */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          <Volume2 className="w-4 h-4" />
          <span>Audio Recording</span>
          {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>

        {/* Expanded player */}
        {expanded && (
          <div className="mt-2 bg-gray-50 rounded-lg border border-gray-200 p-3">
            {loading && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Loading audio...</span>
              </div>
            )}

            {notAvailable && (
              <p className="text-sm text-gray-400">No recording available for this encounter.</p>
            )}

            {error && (
              <div className="flex items-center gap-2 text-sm text-red-500">
                <AlertCircle className="w-4 h-4" />
                <span>{error}</span>
                <button
                  onClick={fetchPlaybackUrl}
                  className="ml-2 text-blue-500 hover:text-blue-700 underline"
                >
                  Retry
                </button>
              </div>
            )}

            {playbackData && (
              <>
                <audio
                  ref={audioRef}
                  src={playbackData.url}
                  preload="auto"
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  onTimeUpdate={() => {
                    if (audioRef.current) setCurrentTime(audioRef.current.currentTime);
                  }}
                  onLoadedMetadata={() => {
                    if (audioRef.current) setDuration(audioRef.current.duration);
                  }}
                  onEnded={() => setIsPlaying(false)}
                  onError={(e) => {
                    const audio = e.currentTarget;
                    const code = audio.error?.code;
                    const msg = audio.error?.message;
                    console.error('Audio playback error:', { code, msg, src: playbackData.url.substring(0, 80) });
                    setError(`Audio error: ${msg || `code ${code}`}`);
                  }}
                />

                <div className="flex items-center gap-3">
                  {/* Play/Pause */}
                  <button
                    onClick={handlePlayPause}
                    className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-blue-500 hover:bg-blue-600 text-white transition-colors"
                  >
                    {isPlaying
                      ? <Pause className="w-4 h-4" />
                      : <Play className="w-4 h-4 ml-0.5" />
                    }
                  </button>

                  {/* Time */}
                  <span className="text-xs text-gray-500 font-mono w-20 text-center flex-shrink-0">
                    {formatTime(currentTime)} / {formatTime(duration || playbackData.durationSeconds)}
                  </span>

                  {/* Progress bar */}
                  <div
                    ref={progressRef}
                    onClick={handleProgressClick}
                    className="flex-1 h-2 bg-gray-200 rounded-full cursor-pointer relative group"
                  >
                    <div
                      className="h-full bg-blue-500 rounded-full transition-[width] duration-100"
                      style={{ width: `${progress}%` }}
                    />
                    <div
                      className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-blue-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      style={{ left: `calc(${progress}% - 6px)` }}
                    />
                  </div>

                  {/* Mute */}
                  <button
                    onClick={toggleMute}
                    className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {muted
                      ? <VolumeX className="w-4 h-4" />
                      : <Volume2 className="w-4 h-4" />
                    }
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  }
);

AudioPlayer.displayName = 'AudioPlayer';
